import {ActionMenu, Image} from '@primer/react-brand/esm'
import {useMemo} from 'react'
import {OS, type OSItem, resolveByOS, resolveSupportedOS, sortByOS} from '@github-ui/get-os'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'
import {useArchitecture} from '@github-ui/swp-core/lib/hooks/use-architecture'
import {useDetectedOS} from '@github-ui/swp-core/lib/hooks/use-detected-os'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import confetti from './confetti.gif'
import {useMonaPixelAnimation} from './use-mona-pixel-animation'

import Styles from './InstallerActionMenu.module.css'

export interface InstallerItem extends OSItem {
  label: string
  href: string
}

export interface InstallerActionMenuProps {
  items: InstallerItem[]
  initialOS?: OS
  location?: string
}

export const resolveDesktopOS = (detectedOS: OS): Extract<OS, 'macOS' | 'Windows' | 'Linux'> =>
  resolveSupportedOS(detectedOS, [OS.macOS, OS.Windows, OS.Linux])

export const resolveShowcaseOS = (detectedOS: OS): Extract<OS, 'macOS' | 'Windows'> => {
  return resolveDesktopOS(detectedOS) === OS.macOS ? OS.macOS : OS.Windows
}

export const InstallerActionMenu = ({items, initialOS, location}: InstallerActionMenuProps) => {
  const detectedOS = useDetectedOS(initialOS)
  const resolvedArch = useArchitecture()
  const prefersReducedMotion = usePrefersReducedMotion()

  const resolvedOS = useMemo(() => resolveDesktopOS(detectedOS), [detectedOS])

  const resolvedInstaller = useMemo(
    () => resolveByOS(items, resolvedOS, resolvedArch),
    [items, resolvedOS, resolvedArch],
  )

  const sortedInstallerItems = useMemo(
    () => sortByOS(items, resolvedOS, resolvedArch),
    [items, resolvedOS, resolvedArch],
  )

  const hasPixelMonaEnabled = isFeatureEnabled('site_ghca_pixel_mona')
  const {animateMonaPixel, downloadStatus, installerActionMenuRef, installerButtonOverlayRef, monaPixelRef} =
    useMonaPixelAnimation({
      enabled: hasPixelMonaEnabled,
      prefersReducedMotion,
      resetKey: resolvedInstaller,
    })

  return (
    <div ref={installerActionMenuRef} className={Styles.InstallerActionMenu}>
      {hasPixelMonaEnabled ? <Image ref={monaPixelRef} className={Styles.MonaPixel} src={confetti} alt="" /> : null}
      <ActionMenu mode="split-button">
        <ActionMenu.Button
          data-testid="installer-action-menu-primary-button"
          variant="primary"
          as="a"
          href={resolvedInstaller?.href}
          {...getAnalyticsEvent({action: resolvedInstaller?.label ?? '', tag: 'a', context: 'installer', location})}
        >
          {resolvedInstaller?.label}
        </ActionMenu.Button>
        <ActionMenu.Overlay aria-label={resolvedInstaller?.label || ''}>
          {sortedInstallerItems.map(item => (
            <ActionMenu.Item
              key={item.label}
              as="a"
              href={item.href}
              onClick={animateMonaPixel}
              {...getAnalyticsEvent({action: item.label, tag: 'a', context: 'installer', location})}
            >
              {item.label}
            </ActionMenu.Item>
          ))}
        </ActionMenu.Overlay>
      </ActionMenu>
      {hasPixelMonaEnabled ? (
        <div ref={installerButtonOverlayRef} className={Styles.ButtonOverlay}>
          <div className={Styles.ButtonOverlayItem} aria-hidden="true" />
          <div className={Styles.ButtonOverlayItem} aria-hidden="true" />
          <div className={Styles.ButtonOverlayItem} aria-hidden="true" />
          <div className={Styles.ButtonOverlayItem} aria-hidden="true" />
          <div className={Styles.ButtonOverlayItem} aria-hidden="true" />
          <div className={Styles.ButtonOverlayText} role="status">
            {downloadStatus}
          </div>
        </div>
      ) : null}
    </div>
  )
}
